import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { doc, onSnapshot } from "firebase/firestore";
import { fetchFaultData, db } from "../utils/firebase";
import { saveAs } from 'file-saver';

import ReactSlider from "react-slider";
import LoadingScreen from "./loadingScreen";
import "./styleSheets/RevenueAnalysis.css";

import { FaExclamationTriangle, FaBolt, FaFilter, FaChevronDown, FaChevronUp, FaDownload } from "react-icons/fa";
import { Modal, Button, TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";

const RevenueAnalysis = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { siteName, selectedDate } = useParams();

    const [kWPerPanel, setKWPerPanel] = useState(0.5);
    const [energyRate, setEnergyRate] = useState(0.12);
    const [faultRevenueData, setFaultRevenueData] = useState([]);
    const [kWhLost, setKWhLost] = useState(0);
    const [panelsAffected, setPanelsAffected] = useState(0);
    const [revenueLost, setRevenueLost] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [revenueRange, setRevenueRange] = useState([0, 1000]);
    const [sliderMaxRevenueLoss, setSliderMaxRevenueLoss] = useState(0);
    const [isFilterVisible, setIsFilterVisible] = useState(true);

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportFileName, setExportFileName] = useState("RevenueData");
    const [exportFormat, setExportFormat] = useState("csv");

    const PVWATTS_API_URL = "https://developer.nrel.gov/api/pvwatts/v6.json";
    const PVWATTS_API_KEY = process.env.REACT_APP_PVWATTS_API_KEY;

    console.log("siteName:", siteName);
    console.log("selectedDate:", selectedDate);



    useEffect(() => {
        if (!siteName) return;

        const siteDocRef = doc(db, "sites", siteName);

        const unsubscribe = onSnapshot(siteDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                setKWPerPanel(data.customFields?.panelProd || 0.5);
                setEnergyRate(data.customFields?.kwValue || 0.12);
            }
        });

        return () => unsubscribe();
    }, [siteName]);

    const toggleFilterVisibility = () => {
        setIsFilterVisible(!isFilterVisible); // Toggle the state
    };

    const fetchPVWattsData = async (lat, lng, systemSize) => {
        try {
            const response = await axios.get(PVWATTS_API_URL, {
                params: {
                    api_key: PVWATTS_API_KEY,
                    lat: lat,
                    lon: lng,
                    system_capacity: systemSize,
                    module_type: 0,
                    losses: 10,
                    array_type: 2,
                    tilt: 0,
                    azimuth: 180,
                },
            });
            return response.data.outputs;
        } catch (error) {
            console.error("Error fetching PVWatts data:", error);
            return null;
        }
    };

    const handleExport = () => {
        let fileContent;
        const fileName = `${exportFileName}.${exportFormat}`;

        if (exportFormat === "csv") {
            fileContent = `Fault ID,Panels Affected,kW Lost,Annual Revenue Lost ($)\n` +
                filteredFaults.map(fault =>
                    `${fault.faultId},${fault.affectedPanels},${fault.faultKW.toFixed(2)},${fault.faultRevenueLoss.toFixed(2)}`
                ).join("\n");
        } else if (exportFormat === "json") {
            fileContent = JSON.stringify(filteredFaults, null, 2);
        } else if (exportFormat === "sql") {
            fileContent = "INSERT INTO faults (fault_id, panels_affected, kw_lost, revenue_lost) VALUES\n" +
                filteredFaults.map(fault =>
                    `('${fault.faultId}', ${fault.affectedPanels}, ${fault.faultKW.toFixed(2)}, ${fault.faultRevenueLoss.toFixed(2)})`
                ).join(",\n") + ";";
        }

        const blob = new Blob([fileContent], { type: exportFormat === "json" ? "application/json" : "text/plain" });
        saveAs(blob, fileName);
        setExportModalOpen(false);
    };

    useEffect(() => {
        const fetchRevenueData = async () => {
            try {
                const faults = await fetchFaultData(siteName, selectedDate);
                if (!faults) throw new Error("No fault data available.");

                const faultMap = {};

                Object.values(faults).forEach((fault) => {
                    const faultId = fault.ID || "Unknown";

                    if (!faultMap[faultId]) {
                        faultMap[faultId] = {
                            faultId,
                            affectedPanels: 0,
                            lat: fault.latitude,
                            lng: fault.longitude,
                        };
                    }

                    faultMap[faultId].affectedPanels += 1;
                });

                const processedFaults = Object.values(faultMap).map((fault) => {
                    const faultKW = fault.affectedPanels * kWPerPanel;
                    const faultRevenueLoss = faultKW * energyRate * 365;

                    return {
                        faultId: fault.faultId,
                        affectedPanels: fault.affectedPanels,
                        faultKW,
                        faultRevenueLoss,
                        lat: fault.lat,
                        lng: fault.lng,
                    };
                });

                processedFaults.sort((a, b) => b.faultRevenueLoss - a.faultRevenueLoss);
                setFaultRevenueData(processedFaults);

                const totalPanelsAffected = processedFaults.reduce((sum, fault) => sum + fault.affectedPanels, 0);
                setPanelsAffected(totalPanelsAffected);

                const impactedKW = totalPanelsAffected * kWPerPanel;
                const pvData = await fetchPVWattsData(
                    processedFaults[0]?.lat,
                    processedFaults[0]?.lng,
                    impactedKW
                );

                if (pvData) {
                    const annualKWhLost = pvData.ac_annual.toFixed(2);
                    setKWhLost(annualKWhLost);
                    setRevenueLost((annualKWhLost * energyRate).toFixed(2));
                }

                const maxLoss = Math.max(...processedFaults.map(f => f.faultRevenueLoss), 1);
                setSliderMaxRevenueLoss(maxLoss);
                setRevenueRange([0, maxLoss]);

                setLoading(false);
            } catch (error) {
                console.error("Error fetching revenue data:", error);
                setError("Failed to fetch revenue data");
                setLoading(false);
            }
        };

        fetchRevenueData();
    }, [siteName, selectedDate, kWPerPanel, energyRate]);

    const filteredFaults = faultRevenueData.filter(
        fault => fault.faultRevenueLoss >= revenueRange[0] && fault.faultRevenueLoss <= revenueRange[1]
    );

    if (loading) return <LoadingScreen />;
    if (error) return <p>{error}</p>;

    return (
        <div className="page-layout">

            <div className="revenue-impact-bar">
                <h2>Total Estimated Revenue Statistics</h2>
                <div className="impact-metrics">
                    <div className="metric-box">
                        <FaExclamationTriangle className="metric-icon" />
                        <div className="metric-text">
                            <span>{panelsAffected.toLocaleString()} Panels Affected</span>
                            <span>({(panelsAffected * kWPerPanel).toFixed(2)} kW)</span>
                        </div>
                    </div>

                    <div className="main-figure-container">
                        <div className="main-figure-label">Estimated Annual Revenue Loss</div>
                        <div className="main-figure">
                            ${parseFloat(revenueLost).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                    </div>

                    <div className="metric-box">
                        <FaBolt className="metric-icon" style={{ color: "#ff5555" }} />
                        <div className="metric-text">
                            <span>{parseFloat(kWhLost).toLocaleString()} kWh lost annually</span>
                        </div>
                    </div>
                </div>
            </div>


            <div>
                <div className="filter-and-export">
                    <button
                        className="toggle-filter-button"
                        onClick={toggleFilterVisibility}
                    >
                        <FaFilter />
                        {isFilterVisible ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    <button className="export-button" onClick={() => setExportModalOpen(true)}>
                        <FaDownload /> Export Table
                    </button>
                </div>

                <div className={`toggle-and-filter-section ${!isFilterVisible ? 'hidden' : ''}`}>
                    <div id="filter-section" className="revenue-filter-inline">
                        <label>Filter Revenue Loss Range:</label>
                        <ReactSlider
                            className="horizontal-slider"
                            thumbClassName="thumb"
                            trackClassName="track"
                            min={0}
                            max={sliderMaxRevenueLoss}
                            value={revenueRange}
                            onChange={setRevenueRange}
                            ariaLabel={['Lower thumb', 'Upper thumb']}
                        />
                        <span>
                            ${revenueRange[0].toLocaleString(undefined, { minimumFractionDigits: 2 })} -
                            ${revenueRange[1].toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                </div>
            </div>

            <div className="fault-revenue-table">
                <h3>Estimated Revenue Lost by Fault</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Fault ID</th>
                            <th>Panels Affected</th>
                            <th>kW Lost</th>
                            <th>Annual Revenue Lost ($)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredFaults.map((fault) => (
                            <tr key={fault.faultId}>
                                <td>{fault.faultId}</td>
                                <td>{fault.affectedPanels}</td>
                                <td>{fault.faultKW.toFixed(2)} kW</td>
                                <td>${fault.faultRevenueLoss.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Export Modal */}
            <Modal open={exportModalOpen} onClose={() => setExportModalOpen(false)}>
                <div className="export-modal">
                    <h3>Export Data</h3>
                    <TextField
                        label="File Name"
                        variant="outlined"
                        value={exportFileName}
                        onChange={(e) => setExportFileName(e.target.value)}
                    />
                    <FormControl sx={{ width: "100%", mt: 2 }}>
                        <InputLabel id="format-label">Format</InputLabel>
                        <Select
                            labelId="format-label"
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value)}
                            label="Format"
                            sx={{
                                backgroundColor: "white",
                                "&.MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#285335",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#0f1f14",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#09130c",
                                    },
                                },
                            }}
                        >
                            <MenuItem value="csv">CSV</MenuItem>
                            <MenuItem value="json">JSON</MenuItem>
                            <MenuItem value="sql">SQL</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        sx={{
                            backgroundColor: "#285335", // Set background color
                            "&:hover": {
                                backgroundColor: "#1f4028", // Darker green on hover
                            },
                        }}
                    >
                        Export
                    </Button>

                </div>
            </Modal>
        </div>
    );
};

export default RevenueAnalysis;
